import { tgxApi } from "../../../app/tgxApi";
import { getFileName, serialize, showToast } from "../../../helpers/utils/Utils";
import {
    getAuthorizationHeader,
    getFileDownloadHeaders,
    handleQueryError,
    handleQueryErrorAndSuccess
} from "../../../helpers/utils/RtkQueryUtils";
import { downloadFile } from "../../../helpers/utils/FileUtils";

export const usersApi = tgxApi.injectEndpoints({
    endpoints: (build) => ({

        searchUsers: build.query({
            query: (data) => {
                let paramData = serialize(data);
                return {
                    url: `/ajax/users?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['users'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        createUser: build.mutation({
            query: (requestBody) => {
                return {
                    url: `/ajax/submit/user`,
                    method: 'POST',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['users'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Created", "Users")
            },
        }),

        validateToken: build.mutation({
            query: (requestBody) => {
                return {
                    url: `/ajax/validate-token`,
                    method: 'POST',
                    body: requestBody
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getUserById: build.query({
            query: (id) => {
                return {
                    url: `/ajax/get-user-by-userId/${id}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['userById'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getAllUsersList: build.query({
            query: () => {
                return {
                    url: `/ajax/get-all-user-list`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['userById'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        updateUser: build.mutation({
            query: ({ id, requestBody }) => {
                return {
                    url: `/ajax/user/update/${id}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['users', 'userById', 'userAuditableInfo'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "User")
            },
        }),

        userLockedUserServices: build.mutation({
            query: (userId) => {
                return {
                    url: `/ajax/user/updateLockedUser/${userId}`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                };
            },
            invalidatesTags: ['users', 'userById', 'userAuditableInfo'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Updated", "User")
            },
        }),

        resetPasswordFromWeb: build.mutation({
            query: (requestBody) => {
                return {
                    url: `/ajax/reset-password-from-user-detail`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['userAuditableInfo'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Reset", "password")
            },
        }),

        resetPasswordFromProfile: build.mutation({
            query: (requestBody) => {
                return {
                    url: `/ajax/reset-password-from-profile`,
                    method: 'PUT',
                    headers: getAuthorizationHeader(),
                    body: requestBody
                };
            },
            invalidatesTags: ['userAuditableInfo'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Reset", "password")
            },
        }),

        resetPassword: build.mutation({
            query: (requestBody) => {
                return {
                    url: `/ajax/reset-password`,
                    method: 'POST',
                    body: requestBody
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Reset", "password")
            },
        }),

        forgotPassword: build.mutation({
            query: (emailId) => {
                return {
                    url: `/ajax/forgot-password/${emailId}`,
                    method: 'POST',
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "", "A password reset email has been sent to the specified email address, if there is a user associated with it")
            },
        }),

        getUserAuditableInfo: build.query({
            query: (requestBody) => {
                let paramData = serialize(requestBody);

                return {
                    url: `/ajax/get-user-auditable-info?${paramData}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['userAuditableInfo'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        getAllUserByTenant: build.query({
            query: (tenant) => {
                return {
                    url: `/ajax/get-user-by-tenant-name/${tenant}`,
                    method: 'GET',
                    headers: getAuthorizationHeader(),
                };
            },
            providesTags: ['userAuditableInfo'],
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        checkIsUserLeaderInTenant: build.query({
            query: (tenantId) => {
                return {
                    url: `/users/is-leader-by-tenant?tenantId=${tenantId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        checkIsUserMemberInTenant: build.query({
            query: (tenantId) => {
                return {
                    url: `/users/is-member-by-tenant?tenantId=${tenantId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        checkIsUserLeaderOfAnyTeam: build.query({
            query: () => {
                return {
                    url: `/users/is-leader`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        checkIsUserMemberOfAnyTeam: build.query({
            query: () => {
                return {
                    url: `/users/is-member`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        impersonateUser: build.mutation({
            query: (username) => {
                const paramData = serialize(username)
                return {
                    url: `/ajax/authenticate/impersonate?${paramData}`,
                    method: 'POST',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled }) {
                queryFulfilled.then((res: any) => {
                    localStorage.setItem("authToken", res?.data?.impersonateUserToken)
                    localStorage.setItem('user', "loggedIn");
                    localStorage.setItem("daysToExpirePassword", JSON.stringify(res?.data?.daysToExpirePassword));
                    if (res?.data?.customAuthTokenDTO?.isUserHaveTeam !== null) {
                        localStorage.setItem(
                            "isUserHaveTeam",
                            JSON.stringify(res?.data?.customAuthTokenDTO?.userHaveTeam)
                        );
                    }
                    localStorage.setItem(
                        "isUserManager",
                        JSON.stringify(res?.data?.customAuthTokenDTO?.isUserManager)
                    );
                }).catch(error => {
                    showToast(error?.error?.data?.message, "error");
                })
            },
        }),

        exportUserData: build.query({
            query: (query) => {
                return {
                    url: `/export/user`,
                    method: 'POST',
                    params: query,
                    headers: getFileDownloadHeaders(),
                    responseType: 'arraybuffer',
                    responseHandler: async (response: { blob: () => any; }) => {
                        const blob = await response.blob();
                        downloadFile(blob, getFileName("users"))
                        return { success: true };
                    },
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),
        deleteUserById: build.mutation({
            query: ({ userId }) => ({
                url: `/ajax/deleteUserById/${userId}`,
                method: 'DELETE',
                headers: getAuthorizationHeader(),
            }),
            invalidatesTags: ['users', 'userAuditableInfo'],
            async onQueryStarted(_, { queryFulfilled }) {
                await handleQueryErrorAndSuccess(queryFulfilled, "Deleted", "User");
            },
        }),
        getTenantCustomerByTenant: build.query({
            query: (tenant) => {
                return {
                    url: `/customers/tenant-customer/${tenant}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        getAllUserListByCustomerId: build.query({
            query: (customerId) => {
                return {
                    url: `/users/user-list/${customerId}`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        getAllAssigneesByUserId: build.query({
            query: (orderId) => {
                return {
                    url: `/users/order-id/${orderId}/assigned-users`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

        getAllMemberUsers: build.query({
            query: () => {
                return {
                    url: `/users/team-members`,
                    method: 'GET',
                    headers: getAuthorizationHeader()
                };
            },
            providesTags: ['memberUsers'],

            async onQueryStarted(_, { queryFulfilled, }) {
                await handleQueryError(queryFulfilled)
            },
        }),

    })
})

export const {
    useSearchUsersQuery,
    useCreateUserMutation,
    useGetUserByIdQuery,
    useUpdateUserMutation,
    useResetPasswordMutation,
    useResetPasswordFromWebMutation,
    useImpersonateUserMutation,
    useGetUserAuditableInfoQuery,
    useForgotPasswordMutation,
    useLazyExportUserDataQuery,
    useResetPasswordFromProfileMutation,
    useGetAllUsersListQuery,
    useGetAllMemberUsersQuery,
    useLazyGetAllUserByTenantQuery,
    useDeleteUserByIdMutation,
    useUserLockedUserServicesMutation,
    useValidateTokenMutation,
    useLazyGetTenantCustomerByTenantQuery,
    useLazyGetAllUserListByCustomerIdQuery,
    useGetAllAssigneesByUserIdQuery
} = usersApi;